import * as Sentry from '@sentry/react';
import { useMachine } from '@xstate/react';
import { GetFormReferralCodeResponseType } from 'libs/shared/data-access/src/services/FormsServices/APIService/types';
import { assign, spawn } from 'xstate';

import { useAuthCoreMachine } from '@nw/auth-core';
import {
  clientIds,
  Config,
  deviceDetailsConstants,
  LogEntriesConstantsEnums,
  LogEntryPriorityEnum,
  LogEntryTypesEnums
} from '@nw/shared/config';
import {
  clearAllClientDomainTokens,
  customEvents,
  DialCodes,
  getAccessToken,
  getNiatApplicationDynamicMutation,
  setAccessTokenToDomain,
  setCollegePredictorAccessTokenToDomain,
  setCollegePredictorRefreshTokenToDomain,
  setFormsAccessTokenToDomain,
  setFormsRefreshTokenToDomain,
  setRefreshTokenToDomain,
  setUserId,
  useCreateIbFormSubmission,
  useCreatePublicIbFormSubmission,
  useFormReferralTrack,
  useGetClientAuthCodeAPI,
  useGetCollegePredictorClientAuthDetails,
  useGetFormReferralCode,
  useGetFormsAuthClientAuthDetails,
  useGetIbFormDetails,
  useGetIbFormSubmissionDetails,
  useGetLoginWithPhoneOTP,
  useGetPredictedCollegeData,
  useGetSignUpWithPhoneOTP,
  useGetUserLogoutAPI,
  useGetUserProfileDetailGraphQL,
  useGetVerifiedUserPhoneOTP,
  useIbUserAccountsDynamicMutationAPI,
  useUpdateUserProfileAPI
} from '@nw/shared/data-access';
import {
  captureLogEntriesExceptionWithScope,
  EMAIL_NOT_REGISTERED,
  formSubmissionAnalytics,
  formVisitedAnalytics,
  getAPIErrorEnum,
  getAPIErrorMessage,
  getDeviceDetailsObjectBasedOnKeys,
  getFrontEndFormPathId,
  getHiddenFormFieldsValues,
  getQueryParamValue,
  getSegmentPropertiesObject,
  getUpdateProfileRequestObject,
  getURLSearchParam,
  INVITE_CODE,
  loadSegmentScript,
  PHONE_NUMBER_NOT_REGISTERED,
  PHONE_NUMBER_NOT_VERIFIED,
  segment,
  sendConfigGAEventToDataLayer,
  sendLoginFlowEventToDataLayer,
  sendSegmentIdentifyAnalytics
} from '@nw/shared/utils';

import {
  AssignConfigEventToContext,
  GoToOTPVerificationEvent,
  SendFrontendSegmentEvent,
  SendGAEvent
} from '../../../product';
import { formSubmissionActionType } from '../../../product/constants/FormConstants';
import { UserProfileDetailsDTO } from '../../../shared/stores/dtos/userProfileDetail.dtos';
import { UserProfileModel } from '../../../shared/stores/models/UserProfileModel';
import { getUserProfileDetailsTransformer } from '../../../shared/stores/transformers/userProfileDetailsTransformer';
import { getValuesWithNormalizedDistrictForUpdateProfile } from '../../../shared/utils/RequestObjectUtils';

import {
  analyticsFormEvents,
  analyticsFormTypes,
  PRIVATE_FORM_SUBMISSION,
  PUBLIC_FORM_SUBMISSION
} from '../../constants/AnalyticsFormEventConstants';
import {
  formsMachineActions,
  formsMachineEvents
} from '../../constants/formMachineConstants';
import { paginationMachine } from '../../machines/PaginationMachine';
import { publicFormsMachine } from '../../machines/PublicFormsMachine';
import {
  FormsMachineContext,
  FormsMachineEvent,
  SendPostMessage
} from '../../machines/PublicFormsMachine/types';
import { PredictedCollegesDetailDTO } from '../../stores/dtos/collegePredictor.dtos';
import { DefaultFormConfigDTO } from '../../stores/dtos/forms.dtos';
import DefaultFormModel from '../../stores/models/FormModels/DefaultForm';
import { getPredictedCollegeDataTransformer } from '../../stores/transformers/CollegePredictorTransform';
import { getFormConfigTransformer } from '../../stores/transformers/formConfigTransformer';
import { DefaultFormTypes } from '../../types/ProductTypes';
import { buildCollegePredictorRequestObject } from '../../utils/CollegePredictorUtils.ts';
import { generateCommonRequestProperties } from '../../utils/FormUtils';

import { getEventDetailsFromEventName } from './publicFormMachineUtils';

interface FormMachineProps {
  formId: string;
  isPublicForm: boolean;
  clientId?: string;
  localProductConfig?: DefaultFormTypes;
}

export type UsePublicMachineReturnType = {
  status: {
    getInitialConfigStatus: string;
  };
  errors: {
    getInitialConfigError: string;
  };
  // TODO:- define the type for publicFormsMachineState and publicFormsMachineSend
  publicFormsMachineState: any;
  publicFormsMachineSend: any;
};

const actions = formsMachineActions;

export const usePublicFormsMachine = (
  props: FormMachineProps
): UsePublicMachineReturnType => {
  const { formId, localProductConfig, isPublicForm, clientId } = props;

  const params = getURLSearchParam();
  const { getAuthCoreMachineWithConfig } = useAuthCoreMachine();

  const { userLogoutAPI } = useGetUserLogoutAPI();

  const { userLoginWithPhoneNumberAPI } = useGetLoginWithPhoneOTP();
  const { getSignUpWithPhoneOTPAPI } = useGetSignUpWithPhoneOTP();
  const { getVerifiedUserPhoneOTPAPI } = useGetVerifiedUserPhoneOTP();
  const { getClientAuthCodeAPI } = useGetClientAuthCodeAPI();
  const { getCollegePredictorClientAuthDetailsAPI } =
    useGetCollegePredictorClientAuthDetails();
  const { getFormsAuthClientAuthDetailsAPI } =
    useGetFormsAuthClientAuthDetails();

  const { getUserProfileDetailGraphQLApi } = useGetUserProfileDetailGraphQL();

  const { updateUserProfileDetailsAPI } = useUpdateUserProfileAPI();
  const { updateMultipleGraphQlMutations } =
    useIbUserAccountsDynamicMutationAPI();

  const { createIbFormSubmissionAPI } = useCreateIbFormSubmission();

  const { createPublicIbFormSubmissionAPI } = useCreatePublicIbFormSubmission();

  const { getIbFormSubmissionDetailsAPI } = useGetIbFormSubmissionDetails();

  const {
    getIbFormDetailsAPI,
    status: getInitialConfigStatus,
    error: getInitialConfigError
  } = useGetIbFormDetails();

  const { getPredictedCollegeDataAPI } = useGetPredictedCollegeData();

  const { getFormReferralCodeApi } = useGetFormReferralCode();

  const { formReferralTrackApi } = useFormReferralTrack();

  const {
    deviceType,
    deviceBrowserName,
    deviceOSName,
    deviceOSVersion,
    deviceBrowserVersion
  } = deviceDetailsConstants;

  const device_details = getDeviceDetailsObjectBasedOnKeys({
    deviceType,
    deviceBrowserName,
    deviceBrowserVersion,
    deviceOSName,
    deviceOSVersion
  });

  //TODO: Give types for context and event  and refactor segment API
  const formsMachineWithConfig = publicFormsMachine(formId).withConfig({
    actions: {
      logoutFromSegment: () => {
        segment.reset();
      },

      assignErrorMessageToContext: assign((context: any, event: any) => ({
        errorMessage: getAPIErrorMessage(event.data.message)
      })),
      setNextAndSubmitBtnLoadingToTrue: assign((context: any, event: any) => ({
        isNextAndSubmitBtnLoading: true
      })),
      setNextAndSubmitBtnLoadingToFalse: assign((context: any, event: any) => ({
        isNextAndSubmitBtnLoading: false
      })),
      [actions.setIsOtpButtonLoadingToFalse]: assign((context, event) => {
        return {
          isOTPButtonLoading: false
        };
      }),
      [actions.setIsOtpButtonLoadingToTrue]: assign((context, event) => {
        return {
          isOTPButtonLoading: true
        };
      }),
      [actions.spawnAuthCoreMachine]: assign((context: any, event) => {
        const { formInfo } = context;
        return {
          authMachineRef: spawn(
            getAuthCoreMachineWithConfig(
              context.values['phone'],
              clientId,
              undefined,
              false,
              undefined,
              formInfo &&
                formInfo.formConfig &&
                formInfo.formConfig.enableOtpAutofill
            ),
            { sync: true }
          )
        };
      }),
      assignFormConfigLocally: assign((context: any, event: any) => {
        const productConfigData: DefaultFormConfigDTO =
          getFormConfigTransformer(localProductConfig as DefaultFormTypes);

        const formInfo: DefaultFormModel = new DefaultFormModel(
          productConfigData
        );
        return {
          formInfo
        };
      }),
      validateDataAndAssignProductConfigToContext: assign(
        (context: FormsMachineContext, event: any) => {
          const productConfigData: DefaultFormConfigDTO =
            getFormConfigTransformer(event.data);

          const productInfo: DefaultFormModel = new DefaultFormModel(
            productConfigData
          );

          return {
            formInfo: productInfo
          };
        }
      ),
      validateAndSetUserProfileDetailsToContext: assign(
        (context: FormsMachineContext, event: any) => {
          const userProfileData =
            event.data.user_profile_details.success_response;

          setUserId(userProfileData.user_id);
          Sentry.setUser({ id: userProfileData.user_id });
          const userProfileDetailsData: UserProfileDetailsDTO =
            getUserProfileDetailsTransformer(userProfileData);

          const userProfileDetails: UserProfileModel = new UserProfileModel(
            userProfileDetailsData
          );

          return {
            userProfileDetails: userProfileDetails
          };
        }
      ),
      assignReferralCodesToContext: assign((context: any, event: any) => ({
        referralCodes: event.data
      })),
      updateInviteCodeInContext: assign((context: any, event: any) => {
        return {
          inviteCode: event.data.invite_code
            ? event.data.invite_code
            : context.inviteCode
        };
      }),
      updateIsInviteCodePresentInBackend: assign(
        (context: any, event: any) => ({
          isInviteCodePresentInBackend: Boolean(event.data.invite_code)
        })
      ),
      assignSelfTransitionToConfigEvents: assign((context: any, event: any) => {
        return {
          eventsFromConfig: [
            formsMachineEvents.doSelfTransition,
            ...context.eventsFromConfig
          ]
        };
      }),
      setGoToNextSteps: assign((context: any, event: any) => {
        if (event.shouldRemove) {
          const goToNextSteps = context.goToNextSteps.slice(1);
          return {
            goToNextSteps: goToNextSteps
          };
        } else {
          return {
            goToNextSteps: [...context.goToNextSteps, 'GO_TO_NEXT_STEP']
          };
        }
      }),
      callEventsFromConfig: (
        context: FormsMachineContext,
        event: FormsMachineEvent
      ) => {
        const { eventsFromConfig } = context;
        if (eventsFromConfig.length > 0) {
          const nextConfigEvents = eventsFromConfig.slice(1);
          const eventDetails = getEventDetailsFromEventName(
            eventsFromConfig[0],
            context.values
          );
          publicFormsMachineSend({
            type: formsMachineEvents.assignConfigEventsToContext,
            configEvent: nextConfigEvents
          } as AssignConfigEventToContext);
          publicFormsMachineSend(eventDetails);
        }
      },
      assignConfigEventsToContext: assign(
        (context: any, event: FormsMachineEvent) => {
          event = event as AssignConfigEventToContext;
          context.eventsFromConfig = event.configEvent;
          if (event.values) {
            context.values = { ...context.values, ...event.values };
          }
        }
      ),
      clearErrorMessageFromContext: assign((context: any, event: any) => {
        return {
          errorMessage: ''
        };
      }),
      resetOTP: assign((context: any, event: any) => {
        return {
          values: {
            ...context.values,
            otp: ''
          }
        };
      }),
      callNextFromContext: (context: any, event: any) => {
        context.next && context.next();
      },
      callBackFromContext: (context, event) => {
        context.back && context.back();
      },
      disableGetOTPButton: assign((context, event) => {
        return {
          shouldShowGetOTP: false
        };
      }),
      enableGetOTPButton: assign((context, event) => {
        return {
          shouldShowGetOTP: true
        };
      }),
      updateIsOtpSentToUserInContext: assign((context, event) => {
        return {
          isOtpSentToUser: true
        };
      }),
      updateFormValuesToContext: assign((context, event: any) => {
        return {
          values: { ...context.values, ...event.values }
        };
      }),
      updateFormSubmittedValues: assign((context, event: any) => {
        return {
          formSubmittedValues: { ...context.values }
        };
      }),
      onSuccessGettingClientAuthCode: assign((context, event: any) => {
        return {
          authCode: event.data.code
        };
      }),
      onSuccessGettingClientAuthCodeForCollegePredictor: assign(
        (context, event: any) => {
          return {
            collegePredictorAuthCode: event.data.code
          };
        }
      ),
      loadSegmentScript: (context, event) => {
        const shouldLoadSegmentScript: boolean | undefined =
          context.formInfo?.formConfig.shouldSendSegmentEvent;

        if (shouldLoadSegmentScript) {
          loadSegmentScript();
        }
      },
      sendIdentifySegmentEvent: (context) => {
        sendSegmentIdentifyAnalytics(
          context.userProfileDetails as UserProfileModel,
          context.values
        );
      },
      sendUserVerifiedOtpSegmentEvent: (context, event) => {
        const { formInfo, values } = context;

        if (
          formInfo &&
          formInfo.formConfig &&
          formInfo.formConfig.shouldSendSegmentEvent &&
          formInfo.formConfig.segmentEventTypes &&
          formInfo.formConfig.segmentEventTypes[
            'verification_success_track_event'
          ]
        ) {
          const verificationSuccessTrackEvent =
            formInfo.formConfig.segmentEventTypes[
              'verification_success_track_event'
            ];
          const hiddenFormFieldsValues = getHiddenFormFieldsValues(
            formInfo.form.allFormFields,
            values
          );
          const extraInfoBasedOnProductConfig = JSON.stringify({
            ...values,
            ...hiddenFormFieldsValues
          });
          const formEventProperties = getSegmentPropertiesObject(
            verificationSuccessTrackEvent,
            {},
            {
              extra_info_based_on_product_config: extraInfoBasedOnProductConfig
            },
            params,
            values
          );
          const trackEventProperties = {
            ...formEventProperties,
            form_id: formId,
            frontend_form_path_id: getFrontEndFormPathId()
          };
          segment.track(
            verificationSuccessTrackEvent.eventName,
            trackEventProperties,
            verificationSuccessTrackEvent.segmentEventIntegrationConfig
          );
        }
      },
      sendFormSubmitSegmentEvent: (context, event) => {
        const { formInfo, values } = context;
        if (
          formInfo &&
          formInfo.formConfig &&
          formInfo.formConfig.shouldSendSegmentEvent &&
          formInfo.formConfig.segmentEventTypes &&
          formInfo.formConfig.segmentEventTypes['success_track_event']
        ) {
          const successTrackEvent =
            formInfo.formConfig.segmentEventTypes['success_track_event'];
          const hiddenFormFieldsValues = getHiddenFormFieldsValues(
            formInfo.form.allFormFields,
            values
          );
          const extraInfoBasedOnProductConfig = JSON.stringify({
            ...values,
            ...hiddenFormFieldsValues
          });
          const formEventProperties = getSegmentPropertiesObject(
            successTrackEvent,
            {},
            {
              extra_info_based_on_product_config: extraInfoBasedOnProductConfig
            },
            params,
            values
          );
          const trackEventProperties = {
            ...formEventProperties,
            form_id: formId,
            frontend_form_path_id: getFrontEndFormPathId()
          };

          segment.track(
            successTrackEvent.eventName,
            trackEventProperties,
            successTrackEvent.segmentEventIntegrationConfig
          );
        }
      },
      sendRegistrationSuccessSegmentEvent: (context, event) => {
        const { formInfo, values } = context;
        if (
          formInfo &&
          formInfo.formConfig &&
          formInfo.formConfig.shouldSendSegmentEvent &&
          formInfo.formConfig.segmentEventTypes &&
          formInfo.formConfig.segmentEventTypes[
            'registration_success_track_event'
          ]
        ) {
          const registrationSuccessTrackEvent =
            formInfo.formConfig.segmentEventTypes[
              'registration_success_track_event'
            ];
          const hiddenFormFieldsValues = getHiddenFormFieldsValues(
            formInfo.form.allFormFields,
            values
          );
          const extraInfoBasedOnProductConfig = JSON.stringify({
            ...values,
            ...hiddenFormFieldsValues
          });
          const formEventProperties = getSegmentPropertiesObject(
            registrationSuccessTrackEvent,
            {},
            {
              extra_info_based_on_product_config: extraInfoBasedOnProductConfig
            },
            params,
            values
          );

          const trackEventProperties = {
            ...formEventProperties,
            form_id: formId,
            frontend_form_path_id: getFrontEndFormPathId()
          };
          segment.track(
            registrationSuccessTrackEvent.eventName,
            trackEventProperties,
            registrationSuccessTrackEvent.segmentEventIntegrationConfig
          );
        }
      },
      sendFormSubmitEventToDataLayer: (context, event) => {
        const { formInfo } = context;
        if (formInfo && formInfo.formConfig) {
          const eventName = isPublicForm
            ? PUBLIC_FORM_SUBMISSION
            : PRIVATE_FORM_SUBMISSION;
          const formType = isPublicForm
            ? analyticsFormTypes.publicEmbed
            : analyticsFormTypes.privateEmbed;
          const { formConfig } = formInfo;
          formSubmissionAnalytics({
            event: eventName,
            form_id: formId,
            form_title: formConfig.title
          });
          formSubmissionAnalytics({
            event: analyticsFormEvents.formSubmission,
            form_id: formId,
            form_title: formConfig.title,
            form_type: formType,
            frontend_form_path_id: getFrontEndFormPathId()
          });
        }
      },
      sendFormVisitEvent: (context, event) => {
        const { formInfo } = context;
        if (formInfo && formInfo.formConfig) {
          const eventName = isPublicForm
            ? analyticsFormEvents.publicFormVisit
            : analyticsFormEvents.privateFormVisit;
          const formType = isPublicForm
            ? analyticsFormTypes.publicEmbed
            : analyticsFormTypes.privateEmbed;
          const { formConfig } = formInfo;
          if (formConfig.shouldSendSegmentEvent) {
            const { pageTrackEvent } = formConfig.segmentEventTypes;
            pageTrackEvent &&
              pageTrackEvent.eventName &&
              segment.track(pageTrackEvent.eventName);
          }

          formVisitedAnalytics({
            event: eventName,
            form_id: formId,
            form_title: formConfig.title
          });
          formVisitedAnalytics({
            event: analyticsFormEvents.formVisit,
            form_id: formId,
            form_title: formConfig.title,
            form_type: formType,
            frontend_form_path_id: getFrontEndFormPathId()
          });
        }
      },
      onSuccessGettingClientAuthenticationDetails: (_context, event: any) => {
        setFormsAccessTokenToDomain(event.data.access_token);
        setFormsRefreshTokenToDomain(event.data.refresh_token);
      },
      onSuccessGettingClientAuthenticationDetailsForCollegePredictor: (
        _context,
        event: any
      ) => {
        setCollegePredictorAccessTokenToDomain(event.data.access_token);
        setCollegePredictorRefreshTokenToDomain(event.data.refresh_token);
      },
      updateOrderDetailsInContext: assign((context: any, event: any) => {
        Sentry.addBreadcrumb({
          type: 'info',
          category: 'updateOrderDetailsInContext',
          message: `set orderDetails`,
          data: {
            orderDetails: event.data
          }
        });
        return {
          orderDetails: event.data
        };
      }),
      sendGetOtpCustomEventToDataLayer: (context, event) => {
        sendLoginFlowEventToDataLayer(customEvents.getOtpForForms, formId, {
          ...device_details
        });
      },
      [actions.sendResendOtpEventToDataLayer]: (_context, _event) => {
        sendLoginFlowEventToDataLayer(customEvents.resendOtp, formId, {
          ...device_details
        });
      },
      updateIsNewUserInContextToTrue: assign((context: any, event) => ({
        isNewUser: true
      })),
      updateIsNewUserInContextToFalse: assign((context: any, event) => ({
        isNewUser: false
      })),
      sendUserVerifiedOtpEvent: (context, event) => {
        if (context.isNewUser) {
          sendLoginFlowEventToDataLayer(
            customEvents.otpVerifySignUpForForms,
            formId,
            {
              ...device_details
            }
          );
        } else {
          sendLoginFlowEventToDataLayer(
            customEvents.otpVerifyLoginForForms,
            formId,
            {
              ...device_details
            }
          );
        }
      },

      updateIsLoggedInInContext: assign((context, event: any) => ({
        isLoggedIn: true
      })),
      setIsOtpVerifiedInContext: assign((context, event: any) => ({
        isOtpVerified: context.isOtpSentToUser ? true : false
      })),
      sendOTPVerifiedFormAnalytics: (context) => {
        const { isOtpVerified, formInfo, values } = context;
        if (
          isOtpVerified &&
          formInfo &&
          formInfo.formConfig &&
          formInfo.formConfig.shouldSendSegmentEvent &&
          formInfo.formConfig.segmentEventTypes &&
          formInfo.formConfig.segmentEventTypes[
            'otp_verified_success_track_event'
          ]
        ) {
          const otpVerifiedSuccessTrackEvent =
            formInfo.formConfig.segmentEventTypes[
              'otp_verified_success_track_event'
            ];
          const hiddenFormFieldsValues = getHiddenFormFieldsValues(
            formInfo.form.allFormFields,
            values
          );
          const extraInfoBasedOnProductConfig = JSON.stringify({
            ...values,
            ...hiddenFormFieldsValues
          });
          const formEventProperties = getSegmentPropertiesObject(
            otpVerifiedSuccessTrackEvent,
            {},
            {
              extra_info_based_on_product_config: extraInfoBasedOnProductConfig
            },
            params,
            values
          );
          const trackEventProperties = {
            ...formEventProperties,
            form_id: formId,
            frontend_form_path_id: getFrontEndFormPathId()
          };
          segment.track(
            otpVerifiedSuccessTrackEvent.eventName,
            trackEventProperties,
            otpVerifiedSuccessTrackEvent.segmentEventIntegrationConfig
          );
        }
      },
      captureSegmentApiFailureSentryException: (context, event: any) => {
        Sentry.captureException(event.data);
      },
      [actions.setCollegePredictorTabsContext]: assign(
        (context, event: any) => {
          const { values } = context;
          let collegePredictorTabs = {};
          if (values?.['tabsDetails']) {
            values?.['tabsDetails'].map((tab: any) => {
              collegePredictorTabs = {
                ...collegePredictorTabs,
                [tab.examNameEnum]: tab
              };
            });
          }
          const initialTab =
            values?.['tabsDetails']?.[0]?.examNameEnum ??
            values?.['entranceExamNameEnum'];

          return {
            collegePredictorTabsInfo: collegePredictorTabs,
            selectedTab: initialTab
          };
        }
      ),
      [actions.updateSelectedTab]: assign((context, event: any) => {
        return {
          selectedTab: event.selectedTab ?? context.selectedTab
        };
      }),
      spawnPaginationMachine: assign((parentMachineContext: any) => {
        const paginationMachineWithConfig = paginationMachine().withConfig({
          actions: {
            initializePaginationMachine: assign((context, event) => {
              const { eachPageLimit } = parentMachineContext;
              return {
                eachPageLimit: eachPageLimit
              };
            }),
            setPaginationData: assign((context, event: any) => {
              const { eachPageLimit, currentPageNo, data } = context;
              if (data[currentPageNo]) {
                return {};
              } else {
                const formattedAndValidated: PredictedCollegesDetailDTO =
                  getPredictedCollegeDataTransformer(event.data);
                const totalPageNo = Math.ceil(
                  formattedAndValidated.totalNoOfColleges / eachPageLimit
                );
                return {
                  totalPageNo: totalPageNo,
                  isNextPagePresent: totalPageNo > 1 ? true : false,
                  data: {
                    ...context.data,
                    totalNoOfColleges: formattedAndValidated.totalNoOfColleges,
                    [currentPageNo]: formattedAndValidated.colleges
                  }
                };
              }
            }),
            setIsDataFetchingTrue: assign((context, event) => {
              return {
                isDataFetching: true
              };
            }),
            setIsDataFetchingFalse: assign((context, event) => {
              return {
                isDataFetching: false
              };
            }),
            setCurrentPageNumber: assign((context, event: any) => {
              const currentPageNo = event.currentPageNo;
              return {
                currentPageNo
              };
            })
          },
          services: {
            fetchPredictedCollegesData: (context, event) => {
              const { currentPageNo, eachPageLimit, data } = context;
              if (data[currentPageNo]) {
                return Promise.resolve(data[currentPageNo]);
              }
              const offset = currentPageNo * eachPageLimit;

              const { values, collegePredictorTabsInfo, selectedTab } =
                parentMachineContext;

              const requestObject = buildCollegePredictorRequestObject({
                values,
                collegePredictorTabsInfo,
                selectedTab,
                formId,
                limit: eachPageLimit
              });

              return getPredictedCollegeDataAPI({
                requestObject,
                offset,
                limit: eachPageLimit
              });
            }
          }
        });
        const { selectedTab, paginationMachineRef } = parentMachineContext;
        return {
          paginationMachineRef: {
            ...paginationMachineRef,
            [selectedTab]: spawn(paginationMachineWithConfig)
          }
        };
      }),
      [actions.sendGAEventToDataLayer]: (context, event) => {
        event = event as SendGAEvent;
        const { formInfo, orderDetails, values } = context;
        const googleAnalyticsEvents =
          formInfo?.formConfig.googleAnalyticsEvents;

        if (googleAnalyticsEvents) {
          sendConfigGAEventToDataLayer(googleAnalyticsEvents, event.eventName, {
            ...values,
            ...orderDetails
          });
        }
      },
      [actions.sendFrontendSegmentEvent]: (context, event) => {
        const { formInfo, values } = context;
        event = event as SendFrontendSegmentEvent;
        const eventName = event.eventName;
        const segmentEventDetail =
          formInfo?.formConfig?.segmentEventTypes &&
          formInfo?.formConfig?.segmentEventTypes[eventName];
        if (
          formInfo?.formConfig?.shouldSendSegmentEvent &&
          segmentEventDetail
        ) {
          const hiddenFormFieldsValues = getHiddenFormFieldsValues(
            formInfo.form.allFormFields,
            values
          );
          const formattedRequestObject = generateCommonRequestProperties({
            values: values as Record<string, any>
          });
          const extraInfoBasedOnProductConfig = JSON.stringify({
            ...formattedRequestObject,
            ...hiddenFormFieldsValues
          });
          const formEventProperties = getSegmentPropertiesObject(
            segmentEventDetail,
            {},
            {
              extra_info_based_on_product_config: extraInfoBasedOnProductConfig
            },
            params,
            formattedRequestObject
          );
          const trackEventProperties = {
            ...formEventProperties,
            form_id: formId,
            frontend_form_path_id: getFrontEndFormPathId()
          };

          segment.track(
            segmentEventDetail.eventName,
            trackEventProperties,
            segmentEventDetail.segmentEventIntegrationConfig
          );
        }
      },
      [actions.sendFormSubmissionSuccessPostMessage]: (context) => {
        const { formInfo } = context;
        if (
          formInfo &&
          formInfo.formConfig &&
          formInfo.formConfig.enablePostMessage
        ) {
          window.parent.postMessage(
            {
              type: 'FORM_SUBMISSION_SUCCESS',
              payload: {
                formId: formId
              }
            },
            '*'
          );
        }
      },
      [actions.sendOverRideFormSubmissionSuccessPostMessage]: (context) => {
        const { formInfo } = context;
        if (
          formInfo &&
          formInfo.formConfig &&
          formInfo.formConfig.enablePostMessage
        ) {
          window.parent.postMessage(
            {
              type: 'OVERRIDE_FORM_SUBMISSION_SUCCESS',
              payload: {
                formId: formId
              }
            },
            '*'
          );
        }
      },
      [actions.sendPublicFormSubmissionSuccessPostMessage]: (context) => {
        const { formInfo } = context;
        if (
          formInfo &&
          formInfo.formConfig &&
          formInfo.formConfig.enablePostMessage
        ) {
          window.parent.postMessage(
            {
              type: 'PUBLIC_FORM_SUBMISSION_SUCCESS',
              payload: {
                formId: formId
              }
            },
            '*'
          );
        }
      },
      [actions.sendPostMessage]: (context, event) => {
        const postMessageEvent = event as SendPostMessage;
        const { formInfo } = context;
        if (
          formInfo &&
          formInfo.formConfig &&
          formInfo.formConfig.enablePostMessage &&
          postMessageEvent.eventName
        ) {
          window.postMessage(
            {
              type: postMessageEvent.eventName,
              payload: {
                formId: formId
              }
            },
            '*'
          );
        }
      },
      [actions.setLoginAccessAndRefreshToken]: (context, event: any) => {
        clearAllClientDomainTokens();
        setAccessTokenToDomain(event.data.accessToken);
        setRefreshTokenToDomain(event.data.refreshToken);
      },
      [actions.onSuccessGettingIbFormSubmissionDetails]: assign(
        (context, event: any) => {
          let formSubmissionDetails: null | Record<string, any> = null;

          event.data.form_submission_details.forEach((data) => {
            if (formSubmissionDetails) {
              formSubmissionDetails = {
                ...formSubmissionDetails,
                ...JSON.parse(data.form_submission_data)
              };
            } else
              formSubmissionDetails = {
                ...JSON.parse(data.form_submission_data)
              };
          });

          return {
            formSubmissionDetails
          };
        }
      ),
      [actions.onSuccessGetFormReferralCode]: assign(
        (context: FormsMachineContext, event: any) => {
          const apiResponse = event.data as GetFormReferralCodeResponseType;
          const referralCode = apiResponse?.referral_code ?? null;
          const status = apiResponse?.status ?? null;
          const isActiveStatus = status === 'ACTIVE';
          const inviteCode = isActiveStatus
            ? referralCode || context.inviteCode
            : context.inviteCode || referralCode || '';
          return {
            inviteCode,
            referralCodes: apiResponse as GetFormReferralCodeResponseType,
            isInviteCodePresentInBackend: Boolean(referralCode)
          };
        }
      ),
      [actions.onErrorTrackFormReferralAttemptLogEntry]: (
        context: FormsMachineContext,
        event: any
      ) => {
        const resStatus = getAPIErrorEnum(event?.data);
        const inviteCodeFromParam = getURLSearchParam().get(INVITE_CODE);
        captureLogEntriesExceptionWithScope(
          LogEntriesConstantsEnums.TRACK_REFERRAL_ATTEMPT_API_FAILED,
          {
            res_status: resStatus,
            requestObject: {
              form_id: formId,
              existing_licence_code: context.referralCodes?.referral_code,
              attempted_licence_code: inviteCodeFromParam
            }
          },
          LogEntryTypesEnums.ERROR,
          LogEntryPriorityEnum.HIGH
        );
      }
    },
    services: {
      logoutUser: (context, event: any) => {
        const isUserLoggedIn = getAccessToken();
        if (isUserLoggedIn) {
          context.isOtpVerified = false;
          return userLogoutAPI();
        } else {
          return Promise.resolve();
        }
      },
      getInitialConfig: (context: any, event: any) => {
        return getIbFormDetailsAPI({ form_id: formId });
      },
      getLoginOtp: (context, event: FormsMachineEvent) => {
        event = event as GoToOTPVerificationEvent;
        const { formInfo } = context;
        return userLoginWithPhoneNumberAPI({
          phoneNumber: context.values?.phone,
          domain:
            formInfo &&
            formInfo.formConfig &&
            formInfo.formConfig.enableOtpAutofill
              ? window.location.host
              : undefined
        });
      },
      getSignUpOTP: (context: any, event) => {
        const { formInfo } = context;
        return getSignUpWithPhoneOTPAPI({
          phoneNumber: context.values.phoneNumber,
          countryCode: DialCodes.india,
          domain:
            formInfo &&
            formInfo.formConfig &&
            formInfo.formConfig.enableOtpAutofill
              ? window.location.host
              : undefined
        });
      },
      getVerifiedUserOTP: (context: any) => {
        const { formInfo } = context;
        return getVerifiedUserPhoneOTPAPI({
          phoneNumber: context.values.phoneNumber,
          countryCode: DialCodes.india,
          domain:
            formInfo &&
            formInfo.formConfig &&
            formInfo.formConfig.enableOtpAutofill
              ? window.location.host
              : undefined
        });
      },
      getClientAuthCode: () =>
        getClientAuthCodeAPI({ client_id: Config['CLIENT_ID'] }),
      getClientAuthenticationDetails: (context: any, event: any) => {
        const clientId = Config['CLIENT_ID'];
        const requestObject = {
          client_id: clientId,
          code: context.authCode
        };
        if (clientId === clientIds.nwPredictorClientId) {
          return getCollegePredictorClientAuthDetailsAPI(requestObject);
        }
        return getFormsAuthClientAuthDetailsAPI(requestObject);
      },

      getCollegePredictorClientAuthCode: () => {
        return getClientAuthCodeAPI({
          client_id: Config['NW_PREDICTOR_CLIENT_ID']
        });
      },

      getCollegePredictorClientAuthenticationDetails: (
        context: any,
        event: any
      ) => {
        const requestObject = {
          client_id: Config['NW_PREDICTOR_CLIENT_ID'],
          code: context.collegePredictorAuthCode
        };
        return getCollegePredictorClientAuthDetailsAPI(requestObject);
      },

      getProfileDetails: () => {
        return getUserProfileDetailGraphQLApi({
          keysToGetInSuccessResponse: []
        });
      },

      updateProfile: (context: any, event: any) => {
        const { userProfileDetails, values } = context;
        return updateUserProfileDetailsAPI(
          getUpdateProfileRequestObject(
            userProfileDetails,
            getValuesWithNormalizedDistrictForUpdateProfile(values)
          )
        );
      },

      updateGraphQlFields: (context: FormsMachineContext) => {
        const {
          values,
          formInfo: { fieldsGraphQlConfig }
        } = context;

        const requestObjectForMutation: Record<string, any> = {};

        fieldsGraphQlConfig &&
          Object.keys(fieldsGraphQlConfig).forEach((eachField) => {
            const fieldGraphQlConfig = fieldsGraphQlConfig[eachField];
            if (values && values[eachField] && fieldGraphQlConfig) {
              const modelName = fieldGraphQlConfig.modelName;
              const fieldName = fieldGraphQlConfig.fieldName;

              if (fieldName === 'phone_number') return;

              requestObjectForMutation[modelName] = {
                ...requestObjectForMutation[modelName],
                [fieldName]: values[eachField]
              };
            }
          });

        const { mutation, data } = getNiatApplicationDynamicMutation(
          'UpdateUserProfileDetails',
          requestObjectForMutation
        );

        if (mutation === '' || !mutation || !data) {
          return new Promise((resolve) => {
            resolve({});
          });
        }

        return updateMultipleGraphQlMutations({
          mutation: mutation,
          data: data
        });
      },

      submitForm: (context, event) => {
        const { otp, current_state, phone, ...values } = context.values ?? {};
        values[INVITE_CODE] = context.inviteCode;
        const formattedRequestObject = generateCommonRequestProperties({
          values
        });

        const attemptedLicenceCode = getQueryParamValue(INVITE_CODE);
        const shouldAddAttemptedLicenceCode =
          context.referralCodes?.referral_code &&
          attemptedLicenceCode &&
          context.referralCodes.referral_code !== attemptedLicenceCode;

        if (shouldAddAttemptedLicenceCode) {
          formattedRequestObject.attempted_licence_code = attemptedLicenceCode;
        }

        const requestObject = {
          form_id: formId,
          form_submission_data: JSON.stringify(formattedRequestObject),
          submission_action: formSubmissionActionType.createAsNew
        };
        return createIbFormSubmissionAPI(requestObject);
      },
      overRideFormSubmission: (context, event) => {
        const { otp, current_state, phone, ...values } = context.values ?? {};
        values[INVITE_CODE] = context.inviteCode;
        const formattedRequestObject = generateCommonRequestProperties({
          values
        });
        Sentry.addBreadcrumb({
          type: 'info',
          category: 'overRideFormSubmission',
          message: `orderDetails`,
          data: {
            orderDetails: context.orderDetails
          }
        });
        const requestObject = {
          form_id: formId,
          form_submission_data: JSON.stringify(formattedRequestObject),
          submission_action: formSubmissionActionType.overrideOldSubmission,
          form_submission_id: context.orderDetails.ib_form_submission_id
        };
        return createIbFormSubmissionAPI(requestObject);
      },
      submitPublicForm: (context, event) => {
        const { otp, current_state, ...values } = context.values ?? {};
        values[INVITE_CODE] = context.inviteCode;
        const phoneNumberDetails = values['phoneNumber']
          ? {
              phone_number: values['phoneNumber'],
              country_code: DialCodes.india
            }
          : undefined;
        const requestObject = {
          form_id: formId,
          form_submission_data: JSON.stringify(values),
          phone_number_details: phoneNumberDetails
        };
        return createPublicIbFormSubmissionAPI(requestObject);
      },
      getIbFormSubmissionDetails: (context, event) => {
        const requestObject = {
          form_id: formId
        };
        return getIbFormSubmissionDetailsAPI(requestObject);
      },
      getFormReferralCode: () => {
        return getFormReferralCodeApi({ formId });
      },
      trackFormReferralAttempt: (context: FormsMachineContext) => {
        const inviteCodeFromParam = getURLSearchParam().get(INVITE_CODE);
        if (!inviteCodeFromParam) return Promise.resolve({});
        return formReferralTrackApi({
          form_id: formId,
          existing_licence_code: context.referralCodes?.referral_code,
          attempted_licence_code: inviteCodeFromParam
        });
      }
    },
    guards: {
      shouldLoadScriptLocally: (context: any, event: any) => {
        return localProductConfig ? true : false;
      },
      isNewUserError: (context: any, event: any) => {
        return (
          getAPIErrorEnum(event.data) === PHONE_NUMBER_NOT_REGISTERED ||
          getAPIErrorEnum(event.data) === EMAIL_NOT_REGISTERED
        );
      },
      isPhoneNumberNotVerifiedError: (context: any, event: any) => {
        return getAPIErrorEnum(event.data) === PHONE_NUMBER_NOT_VERIFIED;
      },
      isProductInfoPresent: (context, event: any) => {
        return context.formInfo ? true : false;
      },
      shouldTrackFormReferralAttempt: (
        context: FormsMachineContext,
        event: any
      ) => {
        const referralCodeFromBackend = event?.data?.referral_code;
        const inviteCodeFromQuery = context.inviteCode;
        return Boolean(
          referralCodeFromBackend &&
            inviteCodeFromQuery &&
            referralCodeFromBackend !== inviteCodeFromQuery
        );
      }
    }
  });

  const [publicFormsMachineState, publicFormsMachineSend] = useMachine(
    formsMachineWithConfig
  );

  return {
    publicFormsMachineState,
    publicFormsMachineSend,
    status: {
      getInitialConfigStatus
    },
    errors: {
      getInitialConfigError
    }
  };
};
